import { getInitialSelectedVariant } from '@src/components/product-card/product-card.helpers'
import { IProductCard, IWebsiteProduct } from '@src/components/product-card/product-card.types'
import {
  Filter,
  FilterOptions,
  SelectedFilter,
  UpdateQueryParams,
} from '@src/pages/[...slug]/components/collection/domains/CollectionFilter'
import {
  getIndividualTagStatus,
  ProductTagKey,
} from '@src/quince-components/src/molecules/productCard/productCard.helpers'
import type { TQueryState } from '@src/quince-site-common/src/hooks/useQueryState'
import searchCacheStateManager from '@src/quince-site-common/src/redux/globalStateManager/SearchCacheStateManager'
import {
  Filter as SearchFilter,
  Facets,
  FilterAttribute,
  FilterOperation,
} from '@src/quince-site-common/src/services/sandSearchService/searchService.types'
import { isBrowser } from '@src/quince-site-common/src/utils/windowUtil'
import { TELEMETRY_EVENT_NAMES } from '@src/telemetry/telemetry-events.constants'
import { trackEvent } from '@src/telemetry/track'
import { transformTagsIntoAnalyticsTags } from '@src/utils/general.utils'

import { PRODUCTS_LIMIT_FOR_EVENT, SEARCH_WATCHED_QUERY_KEYS } from './searchResults/searchResults.constants'
import { isItemInWishList } from './searchResults/searchResults.helpers'

const DEFAULT_SELECTION_TYPE = 'Checkbox'

// This map is maintained to support backward compatibility for URL.
export const FILTER_TO_FACET_KEYS = {
  [FilterAttribute.COLOR]: 'colors',
  [FilterAttribute.PRICE]: 'prices',
  [FilterAttribute.DEPARTMENT]: 'departments',
  [FilterAttribute.CATEGORY]: 'categories',
  [FilterAttribute.MATERIAL]: 'materials',
  [FilterAttribute.SIZE]: 'sizes',
} as const

const FACET_TO_FILTER_KEYS = Object.fromEntries(
  Object.entries(FILTER_TO_FACET_KEYS).map(([key, value]) => [value, key]),
) as Record<string, FilterAttribute>

export const getFilterAttributeFromAPIKey = (filterAttribute: FilterAttribute): string => {
  return FILTER_TO_FACET_KEYS[filterAttribute]
}

export const getAPIFilterKey = (stringKey: string): FilterAttribute => {
  return FACET_TO_FILTER_KEYS[stringKey] ?? (stringKey as FilterAttribute)
}

export function getFilterOperation(key: FilterAttribute) {
  if (key === FilterAttribute.PRICE) {
    return FilterOperation.RANGE
  }

  return FilterOperation.EQ
}

export function convertFacetsToInitialFilters(facetsList?: Facets[]): Filter[] {
  if (!facetsList) return []

  return facetsList
    .map(facet => {
      const values = facet.valueMetadata.map(metadata => {
        const filterValue = new FilterOptions({
          name: metadata.value,
          displayName: metadata.displayName,
          value: metadata.value,
          hex: metadata.hex,
          imgURL: metadata.imgURL,
        })
        return filterValue
      })

      return new Filter({
        name: getFilterAttributeFromAPIKey(facet.key),
        config: {
          columnsToShow: facet.config.columnsToShow ?? 1,
          label: facet.config.label,
          type: facet.config.selectionType ?? DEFAULT_SELECTION_TYPE,
        },
        values,
      })
    })
    .filter(filter => filter.values.length > 0)
}

// Convert selected search filters to API filter format
export const getSearchFilters = (selectedFilters: SelectedFilter): SearchFilter[] => {
  const filters: SearchFilter[] = []

  Object.keys(selectedFilters).forEach(key => {
    const filterKey = getAPIFilterKey(key)
    const values = SelectedFilter.getSelectedFilterValuesByFilterName(selectedFilters, key)
    if (values.length > 0) {
      filters.push({
        key: filterKey,
        value: values,
        operation: getFilterOperation(filterKey),
      })
    }
  })

  return filters
}

// Handle filter Updates
export const getUpdatedSelectedFilters = ({
  filter,
  selectedFilters,
}: {
  filter: UpdateQueryParams['filter']
  selectedFilters: SelectedFilter
}) => {
  let updatedSelectedFilters = selectedFilters
  if (filter) {
    switch (filter.type) {
      case 'remove': {
        updatedSelectedFilters = SelectedFilter.removeValueFromSelectedFilter(
          selectedFilters,
          filter.value,
          filter.name,
        )
        break
      }
      case 'toggle': {
        updatedSelectedFilters = SelectedFilter.toggleValueFromSelectedFilter(
          selectedFilters,
          filter.name,
          filter.value,
        )
        trackEvent(TELEMETRY_EVENT_NAMES.FILTER_CLICK, {
          label: filter.name,
          value: filter.value,
          context: {
            isSelected: SelectedFilter.isFilterValueSelected(updatedSelectedFilters, filter.name, filter.value),
            selectedFilters: updatedSelectedFilters,
          },
        })
        break
      }
      case 'reset': {
        updatedSelectedFilters = new SelectedFilter({})
        break
      }
      default:
        break
    }
  }

  return updatedSelectedFilters
}

export const getSearchCompletedEventData = ({
  data,
  searchQuery,
  numberOfSearchResults,
}: {
  data: IWebsiteProduct[]
  searchQuery: string
  numberOfSearchResults: number
}) => {
  if (data.length) {
    let oos_product_count = 0
    const products = data.slice(0, PRODUCTS_LIMIT_FOR_EVENT).map((product, index) => {
      const { classification, relatedIds, title, colorVariants, reviews } = product
      const initialSelectedVariant = getInitialSelectedVariant({ cardVariants: product.cardVariants })
      let productData = {
        product_id: relatedIds.spid,
        name: title,
        position: index + 1,
        color: initialSelectedVariant?.displayConfig.value,
        count_color_variant: colorVariants?.length,
        rating: reviews.averageRating,
        tags: initialSelectedVariant?.tags?.map(item => item.value) ?? [],
        tag_ids: transformTagsIntoAnalyticsTags(initialSelectedVariant?.tags?.map(item => item.value) ?? []),
        department: classification?.department,
        division: classification?.division,
        subdepartment: classification?.subdepartment,
        wishListed: isItemInWishList(product),
      }
      // eslint-disable-next-line i18next/no-literal-string
      if (productData.tags.includes('Sold Out')) {
        oos_product_count++
      }
      productData = { ...productData, ...getIndividualTagStatus(productData.tags as ProductTagKey[]) }

      return productData
    })

    return {
      products,
      result_count: products.length,
      null_search: false,
      oos_product_count,
      searchQuery,
      queryLength: searchQuery.length,
      numberOfSearchResults,
    }
  } else {
    return {
      null_search: true,
      result_count: 0,
      products: [],
      searchQuery,
      queryLength: searchQuery.length,
      numberOfSearchResults,
    }
  }
}

export interface SearchProductCard extends IProductCard {
  /**
   * The Algolia queryId of the response that returned this specific card — NOT the sitewide "latest"
   * queryId, which `siteStateManager`'s single global field gets overwritten with on every subsequent
   * fetch (filter change / load-more). Reading the global value at click time pairs an OLD card's
   * objectID with a NEWER search's queryId once pagination has run, corrupting Algolia attribution
   * (STRFRNT-16013 review). Baked in per-card at the point each batch is fetched instead.
   */
  searchAlgoliaQueryId?: string
}

// Identity of a card across filter swaps and load-more pages: the product plus the variant it opens on.
// Index-suffixed keys remount every card whose position changes; a bare productId collides when the
// same product appears once per variant. Reads `cardVariants[0].variantId`, present on the legacy,
// DS and PL-SVD card shapes alike (SVD `displayConfig` can be null, so it is not used here).
export const getSearchCardKey = (productCard: SearchProductCard): string => {
  const variantId = (productCard.product as { cardVariants?: { variantId?: number }[] }).cardVariants?.[0]?.variantId
  return `${productCard.product.productId}_${variantId ?? ''}`
}

export const transformProductsToProductCards = (
  products: IWebsiteProduct[],
  searchAlgoliaQueryId?: string,
): SearchProductCard[] => {
  return products.map(product => ({ product, searchAlgoliaQueryId }))
}

export const scrollToTop = () => {
  window.scrollTo({ top: 0, behavior: 'smooth' })
}

export const SEARCH_RESULTS_SECTION_ID = 'newSearchSection'
// Room for the sticky header so the first row is not tucked under it after the jump.
const SEARCH_RESULTS_SCROLL_BUFFER_PX = 80

// Treatment-bucket replacement for `scrollToTop` on a filter click: the grid stays mounted, so only
// bring its top back into view when the user has scrolled past it, and do it instantly — a smooth
// scroll animates over the same frames the results swap needs to paint.
export const scrollSearchResultsIntoViewIfAbove = () => {
  const section = document.getElementById(SEARCH_RESULTS_SECTION_ID)
  if (!section) return

  const { top } = section.getBoundingClientRect()
  if (top >= 0) return

  window.scrollTo({ top: Math.max(window.scrollY + top - SEARCH_RESULTS_SCROLL_BUFFER_PX, 0), behavior: 'auto' })
}

// Partial: `useAllQueryState` omits keys that are absent from the URL rather than setting `undefined`.
export type TSearchQueryState = Partial<TQueryState<(typeof SEARCH_WATCHED_QUERY_KEYS)[number]>>

// Keyed off the watched query STATE, never `useSearchParams()`: the URL trails a filter click by a
// render (`router.push` is async), so a URL-derived key answers "was the PREVIOUS URL cached?" and a
// warm hit restores stale results instead of fetching. Skipping falsy values keeps `''` and a missing
// key equivalent, matching how `useAllQueryState` parses the URL.
export const getSearchCacheKey = (watchedQueryStates: TSearchQueryState) => {
  let cacheKey = ''
  SEARCH_WATCHED_QUERY_KEYS.forEach(key => {
    const value = watchedQueryStates[key]
    if (value) {
      cacheKey += `${key}=${value}&`
    }
  })

  return cacheKey
}

export const resetSearchCacheOnFilterChange = (updatedWatchedQueryState: TSearchQueryState) => {
  if (!isBrowser()) return

  const cacheKey = getSearchCacheKey(updatedWatchedQueryState)
  const searchCache = searchCacheStateManager.getSearchCache(cacheKey)
  if (searchCache) {
    searchCacheStateManager.publishSearchCache(cacheKey, 0)
  }
}
